import { Search } from "@mui/icons-material";
import {
  alpha,
  Box,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { openicon } from "assets";
import { useState } from "react";
import { IKnowledgeCard } from "./types";

interface ISectionGridProps {
  cards: Array<IKnowledgeCard>;
}

// Splits a title into plain/matched runs so the searched letters can be marked.
// Plain indexOf, not a regex, so query characters like "(" need no escaping.
const highlightMatch = (text: string, query: string) => {
  if (!query) return text;

  const haystack = text.toLowerCase();
  const parts: Array<JSX.Element | string> = [];
  let cursor = 0;

  for (let at = haystack.indexOf(query); at !== -1; at = haystack.indexOf(query, cursor)) {
    if (at > cursor) parts.push(text.slice(cursor, at));
    parts.push(
      <Box
        component="mark"
        key={at}
        sx={{
          px: "1px",
          borderRadius: "4px",
          background: "#fde68a",
          color: "inherit",
        }}
      >
        {text.slice(at, at + query.length)}
      </Box>,
    );
    cursor = at + query.length;
  }

  parts.push(text.slice(cursor));
  return parts;
};

// The card grid every section is drawn with, "All" included. You do NOT need to
// touch this file to add a section — see index.ts for that. Edit it only to
// change how the cards themselves look, which changes them everywhere.
const SectionGrid = ({ cards }: ISectionGridProps) => {
  // Lives here so every section — and every section added later — gets the
  // search box for free. The page clears it by remounting on section change.
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();
  const visibleCards = query
    ? cards.filter((card) => card.title.toLowerCase().includes(query))
    : cards;

  return (
    <Box>
      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search"
        size="small"
        sx={{
          mb: 2.5,
          width: { xs: "100%", sm: 320 },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: "#64748b" }} />
            </InputAdornment>
          ),
        }}
      />

      {visibleCards.length ? (
        <Grid container spacing={2.5}>
          {visibleCards.map((card, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={`${card.href}-${index}`}
              sx={{ display: "flex" }}
            >
              <Paper
                component="a"
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  position: "relative",
                  p: 2.5,
                  borderRadius: "18px",
                  minHeight: 150,
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  textDecoration: "none",
                  background: "rgba(255,255,255,0.75)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.8)",
                  boxShadow: `
                    0 8px 30px rgba(0,0,0,0.08),
                    inset 0 1px 1px rgba(255,255,255,0.6)
                  `,
                  transition: "all 0.4s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    border: "1px solid rgba(255,255,255,0.9)",
                    boxShadow: `
                      0 15px 40px rgba(15,23,42,0.14),
                      inset 0 1px 2px rgba(255,255,255,0.8)
                    `,
                  },
                }}
              >
                {/* Always three lines tall, however short the title is, so every
                    card in the grid ends up the same height. Longer titles are
                    clamped with an ellipsis instead of growing the card. */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#0f172a",
                    mb: 1,
                    fontSize: 15,
                    lineHeight: 1.4,
                    minHeight: "63px",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }}
                >
                  {highlightMatch(card.title, query)}
                </Typography>

                {/* Section tag next to the open icon. A card without label/color
                    still lays out fine — it just draws the icon. */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {card.label && card.color ? (
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: "2px",
                        borderRadius: "6px",
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        letterSpacing: "0.2px",
                        color: card.color,
                        background: alpha(card.color, 0.14),
                        border: `1px solid ${alpha(card.color, 0.3)}`,
                      }}
                    >
                      {card.label}
                    </Box>
                  ) : (
                    <Box />
                  )}
                  <Box
                    component="img"
                    src={openicon}
                    alt=""
                    sx={{ width: 16, height: 16, flexShrink: 0 }}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2" sx={{ color: "#64748b", mt: 2 }}>
          No results for &quot;{search.trim()}&quot;
        </Typography>
      )}
    </Box>
  );
};

export default SectionGrid;
